/**
 * BugBounty.js — teens earn a unique Bug Hunter NFT for every real bug they
 * find, screenshot and report. Card + modal, embedded in Help & Support.
 */
import React, { useState } from 'react';
import { Bug, X, Send, Loader2, CheckCircle, Camera, Trophy } from 'lucide-react';
import { apiClient } from '../api/client';

const STATUS_LABELS = {
  new: { label: 'Under review', cls: 'bg-gray-100 text-gray-600' },
  acknowledged: { label: 'Confirmed — fix in progress', cls: 'bg-amber-100 text-amber-700' },
  fixed_awarded: { label: 'Fixed — NFT awarded! 🏆', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Not a bug', cls: 'bg-gray-100 text-gray-500' },
};

export const BugBountyCard = () => {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const [shot, setShot] = useState('');
  const [state, setState] = useState('idle');
  const [reports, setReports] = useState(null);
  const [leaders, setLeaders] = useState([]);

  React.useEffect(() => {
    apiClient.request('/api/bug-bounty/leaderboard').then(d => setLeaders(d?.leaders || [])).catch(() => {});
  }, []);

  const openModal = () => {
    setOpen(true);
    apiClient.request('/api/bug-bounty/mine').then(d => setReports(d?.reports || [])).catch(() => setReports([]));
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_400_000) { alert('Screenshot too big — max 1.4MB. Try a smaller crop.'); return; }
    const reader = new FileReader();
    reader.onload = () => setShot(String(reader.result || ''));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (desc.trim().length < 10) { alert('Tell us a bit more — at least 10 characters.'); return; }
    setState('sending');
    try {
      await apiClient.request('/api/bug-bounty/report', {
        method: 'POST',
        body: JSON.stringify({ description: desc.trim(), page: window.location.pathname, screenshot_data_url: shot }),
      });
      setState('sent');
      setDesc(''); setShot('');
      apiClient.request('/api/bug-bounty/mine').then(d => setReports(d?.reports || [])).catch(() => {});
      setTimeout(() => setState('idle'), 3500);
    } catch (e) { alert(e?.message || 'Could not send.'); setState('idle'); }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto mt-8 rounded-3xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 p-6 flex flex-col sm:flex-row items-center gap-5" data-testid="bug-bounty-card">
        <img src="/bug-hunter-nft.png" alt="Bug Hunter NFT" className="w-24 h-24 rounded-2xl shadow-lg flex-shrink-0" />
        <div className="flex-1 text-center sm:text-left">
          <h3 className="text-lg font-bold text-gray-900">Spot a bug? Earn a one-of-a-kind NFT 🏆</h3>
          <p className="text-sm text-gray-600 mt-1">
            Find something broken, screenshot it, and report it here. When we confirm and fix it,
            a unique <strong>Bug Hunter NFT</strong> drops straight into your Vault.
          </p>
          {leaders.length > 0 && (
            <p className="text-xs mt-2 font-semibold text-purple-700" data-testid="bug-leaderboard">
              🏆 Top Bug Hunters: {leaders.map((l, i) => `${i === 0 ? '👑 ' : `${i + 1}. `}${l.name}${l.is_me ? ' (you!)' : ''} ×${l.bugs}`).join(' · ')}
            </p>
          )}
        </div>
        <button onClick={openModal}
          className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold inline-flex items-center gap-2 flex-shrink-0"
          data-testid="bug-bounty-open">
          <Bug size={16} /> Report a Bug
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" data-testid="bug-bounty-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Bug size={18} className="text-purple-600" /> Report a Bug</h3>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400" data-testid="bug-bounty-close"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 space-y-4 pr-1">
              {state === 'sent' ? (
                <div className="flex items-center gap-2 text-emerald-700 font-semibold py-4" data-testid="bug-bounty-sent">
                  <CheckCircle size={18} /> Sent! We&apos;ll review it — watch your Notifications.
                </div>
              ) : (
                <>
                  <textarea rows={3} value={desc} onChange={e => setDesc(e.target.value)}
                    placeholder="What went wrong? What did you expect to happen?"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-400 outline-none resize-none"
                    data-testid="bug-bounty-desc" />
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 cursor-pointer hover:border-purple-300">
                    <Camera size={18} className="text-purple-500" />
                    <span className="text-sm text-gray-600">{shot ? 'Screenshot attached ✓' : 'Attach your screenshot (required for the NFT!)'}</span>
                    <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="bug-bounty-file" />
                  </label>
                  {shot && <img src={shot} alt="screenshot" className="rounded-xl border border-gray-200 max-h-40 object-contain" />}
                  <button onClick={submit} disabled={state === 'sending'}
                    className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="bug-bounty-submit">
                    {state === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Submit bug report
                  </button>
                </>
              )}

              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5"><Trophy size={12} /> My bug reports</p>
                {reports === null ? (
                  <Loader2 size={18} className="animate-spin text-purple-400" />
                ) : reports.length === 0 ? (
                  <p className="text-xs text-gray-400">None yet — your first catch could be an NFT.</p>
                ) : (
                  <ul className="space-y-2">
                    {reports.map(r => {
                      const s = STATUS_LABELS[r.status] || STATUS_LABELS.new;
                      return (
                        <li key={r.report_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 border border-gray-100" data-testid={`bug-report-${r.report_id}`}>
                          <p className="flex-1 text-xs text-gray-700 line-clamp-1">{r.description}</p>
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold flex-shrink-0 ${s.cls}`}>{s.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
